import React, { useState, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import styles from './EmailInput.module.scss';

/**
 * A single‐line, auto-resizing email input that validates on submit.
 * Shakes and shows a temporary error message when validation fails,
 * and handles any unexpected errors from the submission handler.
 */
export default function EmailInput({
  placeholder = 'name@domain.com',
  onSubmit
}) {
  // If onSubmit isn’t provided or isn’t a function, warn once
  if (typeof onSubmit !== 'function') {
    console.warn('EmailInput: expected onSubmit prop to be a function');
    onSubmit = () => {};
  }

  const [value, setValue] = useState('');
  const [shake, setShake] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const timeoutRef = useRef();
  const taRef = useRef();

  // Auto-resize logic
  useLayoutEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = ta.scrollHeight + 'px';
  }, [value]);

  // Handle the actual “send” action
  const handleClick = async () => {
    const email = value.trim();
    const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    if (!valid) {
      // invalid → shake + show error text
      setShake(true);
      setValue('Ange giltig e-post');
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setValue(''), 2000);
      setTimeout(() => setShake(false), 300);
      return;
    }

    // prevent double‐submits
    if (submitting) return;
    setSubmitting(true);

    try {
      // wrap parent callback in try/catch
      await Promise.resolve(onSubmit(email));
      setValue('');
    } catch (err) {
      console.error('EmailInput onSubmit error:', err);
      // show a user‐friendly fallback
      setShake(true);
      setValue('Något gick fel, försök igen');
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setValue(''), 2000);
      setTimeout(() => setShake(false), 300);
    } finally {
      setSubmitting(false);
    }
  };

  // Clear any pending timeouts as user types
  const handleChange = e => {
    clearTimeout(timeoutRef.current);
    setValue(e.target.value);
  };

  // Enter key → click
  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={`${styles.inputContainer} ${shake ? styles.shake : ''}`}>
        <textarea
          ref={taRef}
          rows={1}
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className={`${styles.textarea} ${shake ? styles.shake : ''}`}
          disabled={submitting}            // disable while submitting
        />
        <button
          onClick={handleClick}
          className={`${styles.button} ${shake ? styles.shake : ''}`}
          aria-label="Skicka mail"
          disabled={submitting}            // disable while submitting
        >
          <img
            src="/assets/input-arrow.svg"
            alt="Send"
            className={styles.icon}
          />
        </button>
      </div>
    </div>
  );
}

EmailInput.propTypes = {
  placeholder: PropTypes.string,
  onSubmit:   PropTypes.func.isRequired,
};








/**
// This was made for development use.
if ( process.env.NODE_ENV === "development" && message === "__EMAIL_INPUT__") 
  {
  setChatMessages((prev) => 
  [
    ...prev,
    {
      role: "assistant",
      type: "emailRequest",
      content: "adam@gmail.com",
    },
  ]);
  setIsTyping(false);
  return;
}
 */